/**
 * Normalize CN code by removing spaces, dots, and hyphens
 * Example: "2507 00 80" -> "25070080"
 * @param {string|number} cnCode - The CN code to normalize
 * @returns {string} - Normalized CN code without delimiters
 */
export function normalizeCnCode(cnCode) {
  if (!cnCode) return '';
  return cnCode.toString().replaceAll(/[\s.-]/g, '');
}

/**
 * Format CN code with spaces for display, NARROWED to CN8.
 * Example: "25070080" -> "2507 00 80"; "7208100090" -> "7208 51 20"'s CN8 root.
 *
 * The narrowing is load-bearing, not incidental: several call sites use the result as a
 * catalogue key against `_id`, which the CBAM goods route serves as 8 digits with 2 spaces.
 * Widening this function is what silently empties those lookups. Where a code must survive
 * whole — a filed TARIC subdivision is the operator's identity — use `formatCnCodeFull`.
 *
 * @param {string|number} cnCode - The CN code to format
 * @returns {string} - Formatted CN8 code with spaces
 */
export function formatCnCode(cnCode) {
  // First normalize, then add spaces
  const stringCnCodeNoDelimiters = normalizeCnCode(cnCode);

  //  add a space after 4th digit then add a space every 2 digits
  return (
    stringCnCodeNoDelimiters.slice(0, 4) +
    ' ' +
    stringCnCodeNoDelimiters.slice(4, 6) +
    ' ' +
    stringCnCodeNoDelimiters.slice(6, 8)
  ).trim();
}

/**
 * Format a CN code with spaces for display, keeping EVERY digit.
 * Example: "25070080" -> "2507 00 80"; "7208100090" -> "7208 10 00 90"
 *
 * The counterpart to `formatCnCode`, for the paths where two TARIC variants of one CN8
 * parent are two different things the operator filed and must stay distinguishable —
 * the diagram's CN allocations, most of all, where collapsing them merges two rows into
 * one and loses a quantity. Never use it as a catalogue key; that is `cbamCnCodeKey`.
 *
 * @param {string|number} cnCode - The CN code to format
 * @returns {string} - Formatted CN code with spaces, at full length
 */
export function formatCnCodeFull(cnCode) {
  const stringCnCodeNoDelimiters = normalizeCnCode(cnCode);
  const parts = [stringCnCodeNoDelimiters.slice(0, 4)];
  for (let index = 4; index < stringCnCodeNoDelimiters.length; index += 2) {
    parts.push(stringCnCodeNoDelimiters.slice(index, index + 2));
  }
  return parts.filter(Boolean).join(' ');
}

/**
 * The catalogue key for a commodity code: delimiters stripped, narrowed to CN8.
 *
 * A printed code is often longer than eight digits — a 10-digit TARIC code
 * carries national subdivisions below the CN level the catalogue is keyed on —
 * and "10 digit codes are also accepted" is a documented promise of the import
 * fields. So `2507 00 80 012` and `2507 00 80` are the same CBAM good, and any
 * lookup has to agree with that. Same narrowing as `formatCnCode`, and as
 * `ukCbamScope.ts` / `ukCbamClassify.js` on the scope path.
 *
 * @param {string|number} cnCode
 * @returns {string} digits-only CN8 root
 */
export function cbamCnCodeKey(cnCode) {
  return normalizeCnCode(cnCode).slice(0, 8);
}

/**
 * Lookup set for "is this a real CBAM CN code", keyed by `cbamCnCodeKey`.
 *
 * The catalogue (`GET /cngoods/cbam`) serves codes spaced — "2507 00 80" — while
 * rows arrive in whatever shape the user's spreadsheet used. Any membership test
 * has to key BOTH sides the same way or it matches nothing at all: a set built
 * from raw catalogue codes and probed with a normalized row code is provably
 * empty of matches, because every catalogue code contains spaces and no
 * normalized code does. That mismatch silently emptied every purchases import
 * for five months.
 *
 * Build the set through here, and probe it with `cbamCnCodeKey`, so the call
 * sites cannot drift apart again.
 *
 * @param {Array<{cnCode?: string|number}>} goods - CBAM goods catalogue
 * @returns {Set<string>} CN8 keys
 */
export function buildCbamCnCodeSet(goods) {
  return new Set((goods || []).map((good) => cbamCnCodeKey(good?.cnCode)).filter(Boolean));
}

export default formatCnCode;
